import { useCallback, useContext } from "react";

import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { store } from "@/store/store";
import type { AssignmentTarget } from "@/store/types";

/** A membership's first and last name from the store, or `null` when it holds none. */
export function membershipName(membershipId: string): string | null {
  const accountId = store.get("workspaceMembership", membershipId)?.accountId;
  const account = accountId ? store.get("account", accountId) : undefined;
  if (!account) return null;
  const name = [account.firstName, account.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return name || account.name || null;
}

/**
 * How the reassignment prototype names a target wherever it shows one: "you" for the viewer, a member's name, a
 * Dispatch view's name for a team, and "DM <code>" for a Driver Manager book.
 */
export function useAssignmentTargetLabel(): (target: AssignmentTarget) => string {
  const { myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const me = myCurrentWorkspaceMembership?.id;
  const { views } = useWebSettings().settings.dispatch;
  return useCallback(
    (target: AssignmentTarget) => {
      switch (target.kind) {
        case "member":
          return target.membershipId === me ? "you" : (membershipName(target.membershipId) ?? "a member");
        case "team":
          return views[target.viewId]?.label ?? "a team";
        case "dm":
          return `DM ${target.code}`;
      }
    },
    [me, views],
  );
}
